/**
 * OOOService — Out-Of-Order service.
 *
 * Tracks the highest-seen timestamp per stream key and tells callers whether
 * an incoming event is fresh enough to apply, so a stale payload arriving
 * after a newer one can be dropped instead of clobbering store state.
 */

import * as logger from '../utils/logger'

const lastSeen: Record<string, number> = {}

/**
 * Reset all stored timestamps. Call when leaving a context where the keys
 * could collide with the next one (e.g. switching rundowns).
 */
export function reset(): void {
  for (const key of Object.keys(lastSeen)) {
    delete lastSeen[key]
  }
}

/**
 * Decide whether an event for `key` should be accepted based on its timestamp,
 * and remember the timestamp if so.
 *
 * Events without a timestamp are always accepted (back-compat for documents
 * that don't yet carry the field).
 *
 * Equal timestamps are accepted: HTTP response and Firestore snapshot for the
 * same write share `serverTimestamp()` and either may arrive first.
 *
 * @param key - Stable identifier for the event stream (e.g. `cell:<id>`).
 * @param ms  - Timestamp of the incoming event, in milliseconds since epoch.
 *              Pass null/undefined for untimed events (always accepted).
 * @returns   - true if the caller should apply the update; false to drop it.
 */
export function accept(key: string, ms: number | null | undefined): boolean {
  if (!ms) return true

  const last = lastSeen[key] ?? 0
  if (ms >= last) {
    lastSeen[key] = ms
    return true
  }

  logger.debug(`[OOOService] dropped stale ${key}`, { incoming: ms, last })
  return false
}
